import { SRGBColorSpace, TextureLoader, type Texture } from 'three';

/**
 * Real frontage photos (hand-captured Street View screenshots, cropped straight-on).
 * Generated by `pnpm assets:facades` into public/assets/facades/ — git-ignored, never published.
 */
interface ManifestEntry {
  file: string;
  width: number;
  height: number;
}

interface Manifest {
  legs: Record<string, ManifestEntry[]>;
}

export interface PhotoFacade {
  texture: Texture;
  /** width / height of the photo. */
  aspect: number;
}

const BASE = `${import.meta.env.BASE_URL}assets/facades/`;
const loader = new TextureLoader();
const byLeg = new Map<string, PhotoFacade[]>();

function load(entry: ManifestEntry): PhotoFacade {
  const texture = loader.load(BASE + entry.file);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  return { texture, aspect: entry.width / entry.height };
}

/** Reads the manifest once at boot. No manifest (no photos yet) is fine: painted facades are used instead. */
export async function loadPhotoFacades(): Promise<number> {
  try {
    const response = await fetch(`${BASE}manifest.json`, { cache: 'no-cache' });
    if (!response.ok) return 0;
    const manifest = (await response.json()) as Manifest;
    let count = 0;
    for (const [leg, entries] of Object.entries(manifest.legs)) {
      byLeg.set(leg, entries.map(load));
      count += entries.length;
    }
    return count;
  } catch {
    return 0;
  }
}

export function photosFor(legId: string): readonly PhotoFacade[] {
  return byLeg.get(legId) ?? [];
}
